<template>
  <div class="connection-settings-tab">
    <div class="tab-header mb-6">
      <h3 class="text-lg font-semibold text-gray-900">Connection Settings</h3>
      <p class="text-sm text-gray-600 mt-1">
        Configure how campaigns are synced and handled in Salesforce
      </p>
    </div>

    <div class="form-sections space-y-6">
      <!-- Lead Creation Notice -->
      <div class="form-section">
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div class="flex items-start space-x-3">
            <i class="fas fa-info-circle text-blue-600 mt-1"></i>
            <div class="text-sm">
              <p class="font-medium text-blue-900 mb-1">Simplified Lead Creation</p>
              <p class="text-blue-800">
                All campaigns are automatically synced as <strong>Leads</strong> in Salesforce. 
                Salesforce will handle the Lead-to-Opportunity conversion process internally if a Contact already exists.
                This ensures proper tracking and attribution in your Salesforce workflow.
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- Salesforce Features Section -->
      <div class="form-section">
        <div class="section-header mb-4">
          <h4 class="text-md font-medium text-gray-800 flex items-center space-x-2">
            <i class="fas fa-cogs text-purple-500"></i>
            <span>Salesforce Features</span>
          </h4>
          <p class="text-sm text-gray-600">
            Enable or disable specific Salesforce functionality during sync
          </p>
        </div>

        <div class="space-y-4">
          <!-- Assignment Rules -->
          <div class="form-group">
            <label class="flex items-start space-x-3">
              <input 
                type="checkbox"
                v-model="localSettings.use_assignment_rules"
                class="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                @change="emitUpdate"
              >
              <div>
                <span class="text-sm font-medium text-gray-700">Use Assignment Rules</span>
                <p class="text-xs text-gray-500 mt-1">
                  Apply Salesforce assignment rules to automatically assign records to users/queues.
                  This requires assignment rules to be configured in your Salesforce org.
                </p>
              </div>
            </label>
          </div>

          <!-- Dry Run Mode -->
          <div class="form-group">
            <label class="flex items-start space-x-3">
              <input 
                type="checkbox"
                v-model="localSettings.dry_run"
                class="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                @change="emitUpdate"
              >
              <div>
                <span class="text-sm font-medium text-gray-700">Dry Run Mode</span>
                <p class="text-xs text-gray-500 mt-1">
                  Test the sync process without actually creating or updating records in Salesforce.
                  Useful for testing and validation.
                </p>
              </div>
            </label>
            
            <div v-if="localSettings.dry_run" class="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
              <i class="fas fa-info-circle text-blue-600 mr-1"></i>
              <span class="text-xs text-blue-700">
                While dry run is enabled, no actual changes will be made to Salesforce
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Summary Section -->
      <div class="form-section">
        <div class="bg-gray-50 rounded-lg p-4">
          <h4 class="text-sm font-medium text-gray-800 mb-3">
            <i class="fas fa-clipboard-list text-gray-600 mr-2"></i>
            Configuration Summary
          </h4>
          <div class="space-y-2 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-600">Record Type:</span>
              <span class="font-medium text-green-600">Always creates Leads</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Assignment Rules:</span>
              <span class="font-medium" :class="localSettings.use_assignment_rules ? 'text-green-600' : 'text-gray-500'">
                {{ localSettings.use_assignment_rules ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-600">Dry Run Mode:</span>
              <span class="font-medium" :class="localSettings.dry_run ? 'text-yellow-600' : 'text-gray-500'">
                {{ localSettings.dry_run ? 'Enabled' : 'Disabled' }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, watch } from 'vue';

export default {
  name: 'ConnectionSettingsTab',
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const localSettings = ref({
      use_assignment_rules: true,
      dry_run: false,
    });

    const emitUpdate = () => {
      emit('update:modelValue', { ...localSettings.value });
    };

    // Initialize with prop values
    onMounted(() => {
      localSettings.value = {
        use_assignment_rules: props.modelValue.use_assignment_rules ?? true,
        dry_run: props.modelValue.dry_run || false,
      };
    });

    // Watch for external prop changes
    watch(() => props.modelValue, (newValue) => {
      localSettings.value = {
        use_assignment_rules: newValue.use_assignment_rules ?? true,
        dry_run: newValue.dry_run || false,
      };
    }, { deep: true });

    return {
      localSettings,
      emitUpdate,
    };
  },
};
</script>

<style scoped>
.form-select {
  @apply block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500;
}

.form-section {
  @apply border-b border-gray-100 pb-6 last:border-b-0 last:pb-0;
}

.tab-header {
  @apply border-b border-gray-200 pb-4;
}

input[type="radio"]:checked {
  @apply text-blue-600;
}

@media (max-width: 640px) {
  .form-sections {
    @apply space-y-4;
  }
  
  .form-section {
    @apply pb-4;
  }
  
  .space-y-3 > * + * {
    @apply mt-2;
  }
}
</style>